import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { TransactionLogger } from '../../utils/transactionLogger';
import { BracketGenerator, bracketUtils } from '../../utils/bracketGenerator';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  PlayArrow,
  Refresh,
  EmojiEvents
} from '@mui/icons-material';

export default function MatchPlayTournament() {
  const { tournamentId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [winner, setWinner] = useState('');
  const [showResultDialog, setShowResultDialog] = useState(false);

  useEffect(() => {
    fetchTournamentData();
  }, [tournamentId]);

  const fetchTournamentData = async () => {
    try {
      // Fetch tournament
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (tournamentDoc.exists()) {
        const tournamentData = tournamentDoc.data();
        setTournament(tournamentData);
        
        // If bracket exists, load it
        if (tournamentData.bracket) {
          setBracket(tournamentData.bracket);
        }
      }

      // Fetch players
      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournament.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);

    } catch (error) {
      console.error('Error fetching tournament data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateBracket = () => {
    if (players.length < 2) {
      alert('Need at least 2 players to generate bracket');
      return;
    }

    const generator = new BracketGenerator(players, tournamentId);
    const bracketData = generator.generateBracket();
    
    setBracket(bracketData);
    setActiveStep(1);
    
    // Save bracket to tournament
    updateTournamentBracket(bracketData);
  };

  const updateTournamentBracket = async (bracketData) => {
    try {
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        bracket: bracketData,
        status: 'bracket_generated'
      });
    } catch (error) {
      console.error('Error updating tournament bracket:', error);
    }
  };

  const startTournament = async () => {
    try {
      const updates = {
        status: 'active',
        startedAt: new Date()
      };
      
      // Log the status change transaction
      await TransactionLogger.logTransaction(
        'STATUS_CHANGE',
        'TOURNAMENT',
        tournamentId,
        tournament,
        { ...tournament, ...updates },
        currentUser.uid,
        userProfile?.email
      );
      
      await updateDoc(doc(db, 'tournaments', tournamentId), updates);
      setTournament(prev => ({ ...prev, ...updates }));
      setActiveStep(2);
    } catch (error) {
      console.error('Error starting tournament:', error);
    }
  };

  const handleMatchResult = (match, winner) => {
    setSelectedMatch(match);
    setWinner(winner);
    setShowResultDialog(true);
  };

  const submitMatchResult = async () => {
    if (!selectedMatch || !winner) return;

    try {
      // Update bracket with match result
      const updatedBracket = { ...bracket };
      const match = updatedBracket.brackets[selectedMatch.roundIndex][selectedMatch.matchIndex];
      match.winner = winner;
      match.status = 'completed';

      // Advance winner to next round
      advanceWinner(updatedBracket, selectedMatch.roundIndex, selectedMatch.matchIndex, winner);

      setBracket(updatedBracket);
      await updateTournamentBracket(updatedBracket);

      setShowResultDialog(false);
      setSelectedMatch(null);
      setWinner('');

    } catch (error) {
      console.error('Error submitting match result:', error);
    }
  };

  const advanceWinner = (bracket, roundIndex, matchIndex, winner) => {
    const nextRoundIndex = roundIndex + 1;
    if (nextRoundIndex < bracket.brackets.length) {
      const nextMatchIndex = Math.floor(matchIndex / 2);
      const nextMatch = bracket.brackets[nextRoundIndex][nextMatchIndex];
      
      if (nextMatch) {
        if (matchIndex % 2 === 0) {
          nextMatch.player1 = winner;
        } else {
          nextMatch.player2 = winner;
        }
        
        if (nextMatch.player1 && nextMatch.player2) {
          nextMatch.status = 'ready';
        }
      }
    }
  };

  const getActiveMatches = () => {
    if (!bracket) return [];
    
    const activeMatches = [];
    for (let roundIndex = 0; roundIndex < bracket.brackets.length; roundIndex++) {
      const round = bracket.brackets[roundIndex];
      for (let matchIndex = 0; matchIndex < round.length; matchIndex++) {
        const match = round[matchIndex];
        if (match.status === 'ready' && !match.winner) {
          activeMatches.push({
            ...match,
            roundIndex,
            matchIndex
          });
        }
      }
    }
    return activeMatches;
  };

  const getTournamentWinner = () => {
    if (!bracket) return null;
    
    const finalRound = bracket.brackets[bracket.brackets.length - 1];
    if (finalRound && finalRound.length > 0) {
      const finalMatch = finalRound[0];
      return finalMatch.winner;
    }
    return null;
  };

  const renderBracketVisualization = () => {
    if (!bracket) return null;

    return (
      <Grid container spacing={2}>
        {bracket.brackets.map((round, roundIndex) => (
          <Grid item xs={12} md={6} lg={3} key={roundIndex}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Round {roundIndex + 1}
                </Typography>
                {round.map((match, matchIndex) => (
                  <Box key={match.id} mb={2}>
                    <Card variant="outlined">
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body2">
                              {match.player1?.name || 'TBD'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              vs
                            </Typography>
                            <Typography variant="body2">
                              {match.player2?.name || 'TBD'}
                            </Typography>
                          </Box>
                          <Box>
                            {match.status === 'ready' && !match.winner && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleMatchResult({ ...match, roundIndex, matchIndex }, null)}
                              >
                                Enter Result
                              </Button>
                            )}
                            {match.winner && (
                              <Chip
                                label={`Winner: ${match.winner.name}`}
                                color="success"
                                size="small"
                              />
                            )}
                            {match.status === 'bye' && (
                              <Chip
                                label="BYE"
                                color="default"
                                size="small"
                              />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (loading) {
    return <Container><Typography>Loading tournament...</Typography></Container>;
  }

  if (!tournament) {
    return <Container><Typography>Tournament not found</Typography></Container>;
  }

  const steps = ['Setup', 'Generate Bracket', 'Play Matches', 'Complete'];
  const activeMatches = getActiveMatches();
  const tournamentWinner = getTournamentWinner();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {tournament.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Match Play Tournament
            </Typography>
          </Box>
          <Box>
            <Chip
              label={tournament.status.toUpperCase()}
              color={tournament.status === 'active' ? 'success' : 'default'}
            />
          </Box>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Tournament Setup
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Players ({players.length})
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Handicap</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {players.map(player => (
                            <TableRow key={player.id}>
                              <TableCell>{player.firstName} {player.lastName}</TableCell>
                              <TableCell>{player.handicap || 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Bracket Information
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      Players: {players.length}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      Rounds needed: {bracketUtils.calculateRounds(players.length)}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      BYEs needed: {bracketUtils.calculateByes(players.length)}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Refresh />}
                      onClick={generateBracket}
                      disabled={players.length < 2}
                      sx={{ mt: 2 }}
                    >
                      Generate Bracket
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Bracket Generated
            </Typography>
            {bracket && (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Bracket generated with {bracket.numByes} BYE(s)
                </Alert>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={startTournament}
                  sx={{ mb: 3 }}
                >
                  Start Tournament
                </Button>
                {renderBracketVisualization()}
              </Box>
            )}
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Active Matches
            </Typography>
            {activeMatches.length > 0 ? (
              <Grid container spacing={2}>
                {activeMatches.map((match, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Round {match.round} - Match {match.position + 1}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body1">
                              {match.player1?.name} vs {match.player2?.name}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            onClick={() => handleMatchResult(match, null)}
                          >
                            Enter Result
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info">
                No active matches. Tournament may be complete.
              </Alert>
            )}
            {renderBracketVisualization()}
          </Box>
        )}

        {activeStep === 3 && (
          <Box textAlign="center">
            <EmojiEvents sx={{ fontSize: 80, color: 'gold', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Tournament Complete!
            </Typography>
            {tournamentWinner && (
              <Typography variant="h6" color="primary">
                Winner: {tournamentWinner.name}
              </Typography>
            )}
          </Box>
        )}

        {/* Match Result Dialog */}
        <Dialog open={showResultDialog} onClose={() => setShowResultDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Enter Match Result</DialogTitle>
          <DialogContent>
            <Typography variant="h6" gutterBottom>
              {selectedMatch?.player1?.name} vs {selectedMatch?.player2?.name}
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>Winner</InputLabel>
              <Select
                value={winner}
                label="Winner"
                onChange={(e) => setWinner(e.target.value)}
              >
                <MenuItem value={selectedMatch?.player1?.id}>
                  {selectedMatch?.player1?.name}
                </MenuItem>
                <MenuItem value={selectedMatch?.player2?.id}>
                  {selectedMatch?.player2?.name}
                </MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowResultDialog(false)}>Cancel</Button>
            <Button onClick={submitMatchResult} variant="contained">
              Submit Result
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
}

